import { Box } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import {
  StyledAiMessage,
  StyledAiThinking,
  StyledMessageTime,
  StyledSystemMessage,
  StyledUserMessage,
  StyledUserOptionsMessage,
} from "./TestGround.styles";
import { useIntl } from "react-intl";
import { formatDate } from "features/helpers/utils";
import { StyledThumbIcon } from "features/metadesk/ticket/Chat.styles";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import useGetTrainingData from "../trainingArea/useGetTrainingData";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import TrainingEditModal from "../trainingArea/TrainingEditModal";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";

export default function MessageItem({
  item,
  index,
  handleSendMessage,
  messagesList,
  handleRefetchTrainingData,
}) {
  const {
    _id,
    isAdminResponse,
    text,
    time,
    adminFeedback,
    userFeedback,
    isTexting,
    aiRespondsTo,
    isThinking,
    sources,
  } = item || {};

  const sendTime = formatDate(time, { time: true });
  const { formatMessage } = useIntl();

  const { setCanUpdateResponse } = useGetTrainingData();

  const [thumb, setThumb] = useState(null);
  const [editInfo, setEditInfo] = useState(null);

  const isTextLoading = typeof text !== "string";

  useEffect(() => {
    if (adminFeedback) {
      return setThumb(adminFeedback);
    }
    if (userFeedback) {
      return setThumb(userFeedback);
    }
  }, [item]);

  const dispatchSnackbar = useDispatchSnackbar();
  const [entityName] = useOutletContext();

  async function handleThumbUp() {
    if (thumb === "thumbUp") return;
    try {
      await axios.post(`${apiUrl}/api/trainingArea/${entityName}/messages`, {
        messageId: _id,
        adminFeedback: "thumbUp",
      });
      setThumb("thumbUp");
      if (handleRefetchTrainingData) {
        handleRefetchTrainingData();
      }
    } catch (err) {
      dispatchSnackbar({ err });
    }
  }

  async function handleThumbDown() {
    try {
      if (thumb === "thumbDown") return;
      await axios.post(`${apiUrl}/api/trainingArea/${entityName}/messages`, {
        messageId: _id,
        adminFeedback: "thumbDown",
      });
      setThumb("thumbDown");
      setEditInfo(item);
    } catch (err) {
      dispatchSnackbar({ err });
    }
  }

  if (!_id && !isTextLoading) return null;

  const uniqueTitles = new Set();
  let hasDefaultKnowledge = false;

  const displaySources =
    sources
      ?.filter((source) => {
        // 如果不是用戶知識庫（即 defaultKnowledge）
        if (!source.isUserKB) {
          if (hasDefaultKnowledge) {
            return false; // 已經有 defaultKnowledge 了，過濾掉
          }
          hasDefaultKnowledge = true;
          return true;
        }

        // 如果是用戶知識庫，按 title 去重
        if (uniqueTitles.has(source.title)) {
          return false;
        }
        uniqueTitles.add(source.title);
        return true;
      })
      .slice(0, 6) || [];

  return (
    <Fragment key={_id}>
      {isAdminResponse ? (
        <>
          <Box
            mb="8px"
            mt="14px"
            fontSize={10}
            color="#777"
            className="item-flex"
          >
            <img
              src={require("assets/img/ic_ai.svg").default}
              style={{
                width: "21px",
                height: "21px",
                marginRight: "3px",
              }}
            />
            {formatMessage({
              id: "global.aiSupport.toggle.label",
            })}
          </Box>
          {/* ai opening message */}
          <Box ml="20px">
            {isThinking ? (
              <StyledAiMessage>
                <StyledAiThinking>
                  <span />
                  <span />
                  <span />
                </StyledAiThinking>
              </StyledAiMessage>
            ) : (
              <StyledAiMessage dangerouslySetInnerHTML={{ __html: text }} />
            )}
            <StyledMessageTime
              isAdminResponse
              className="item-flex"
              gap="0 6px"
            >
              {sendTime}
              {!isTexting && aiRespondsTo && (
                <Box className="item-flex" gap="0 6px">
                  <StyledThumbIcon
                    className="meta-crm-icon-ic_thumb-up font-size-14"
                    active={thumb === "thumbUp"}
                    onClick={handleThumbUp}
                  />
                  <StyledThumbIcon
                    className="meta-crm-icon-ic_thumb-down font-size-14"
                    active={thumb === "thumbDown"}
                    onClick={handleThumbDown}
                  />
                </Box>
              )}
              {displaySources.length > 0 && (
                <Box className="item-flex" gap="4px" ml="4px">
                  {displaySources.map((source, sourceIndex) => (
                    <IconTooltip
                      key={sourceIndex}
                      content={formatMessage(
                        {
                          id: "aiTestGround.source.replyGeneratedBasedOn",
                        },
                        {
                          title: source.isUserKB ? (
                            <span
                              style={{
                                wordBreak: "break-all",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {source.title}
                            </span>
                          ) : (
                            formatMessage({
                              id: "aiTestGround.source.defaultKnowledge",
                            })
                          ),
                        }
                      )}
                      icon={
                        <Box
                          component="span"
                          sx={{
                            fontSize: "10px",
                            fontWeight: 500,
                            cursor: "pointer",
                            padding: "0 1px",
                          }}
                        >
                          [{sourceIndex + 1}]
                        </Box>
                      }
                      customColor="#A5A5A5"
                      placement="bottom-start"
                    />
                  ))}
                </Box>
              )}
            </StyledMessageTime>
          </Box>
        </>
      ) : (
        <Box mt="14px">
          {item?.role === "system" ? (
            <StyledSystemMessage>{item.text}</StyledSystemMessage>
          ) : item?.role === "button" ? (
            <>
              {index === messagesList.length - 1 ? (
                <Box
                  display="flex"
                  alignItems="flex-end"
                  flexDirection="column"
                >
                  {item?.data?.map((item) => (
                    <StyledUserOptionsMessage
                      onClick={() => handleSendMessage(item.text)}
                    >
                      {item.text}
                    </StyledUserOptionsMessage>
                  ))}
                </Box>
              ) : null}
            </>
          ) : (
            <>
              <StyledUserMessage dangerouslySetInnerHTML={{ __html: text }} />
              <StyledMessageTime>{sendTime}</StyledMessageTime>
            </>
          )}
        </Box>
      )}
      {editInfo && (
        <TrainingEditModal
          editInfo={editInfo}
          onClose={() => {
            setEditInfo(null);
            if (handleRefetchTrainingData) {
              handleRefetchTrainingData();
            }
          }}
          refresh={handleRefetchTrainingData}
          setCanUpdateResponse={setCanUpdateResponse}
        />
      )}
    </Fragment>
  );
}
